import { supabase } from '../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from './imageCache';
import { mapWithConcurrency } from './concurrency';

/**
 * Max Storage operations in flight at once. Each one takes a Postgres
 * connection from the Storage API pool, so we keep this low to avoid
 * spiking the instance's connection count during batch generations.
 */
const STORAGE_CONCURRENCY = 4;

export type PreloadedImage = {
  // `imagenes_generadas.id` es UUID (string). Aceptamos number por callers
  // legacy, pero en la práctica es un uuid — NUNCA hacer Number(id).
  id: number | string;
  fileName: string;
  signedUrl: string;
  optSignedUrl?: string;
  favorito: boolean;
  /** Nombre de la variante a la que pertenece la imagen — se usa para
   *  el título visible en GeneratedImagesInline y para el nombre del
   *  archivo al descargar. Si la imagen no tiene `variante_id` o el
   *  join no devolvió nada, queda undefined y el caller cae al fallback
   *  "Imagen" / al stem del file_name. */
  variante_nombre?: string;
};

/**
 * Preload generated images by polling for file_name availability,
 * fetching signed URLs, creating opt/ thumbnails, and warming browser cache.
 *
 * This is shared across all generation flows (AddFirstProduct,
 * PruebaNuevaInterfaz, etc.)
 */
/**
 * Cuánto se espera, como MUCHO, a que el navegador traiga UNA imagen.
 *
 * No es el tope de la función: es por imagen, y lo único que se pierde al vencer es tener
 * esa foto ya en la caché del navegador.
 */
const ESPERA_MAX_POR_IMAGEN_MS = 8_000;

export async function preloadGeneratedImages(
  // uuid (string) en la práctica; number tolerado por callers legacy.
  imageIds: Array<number | string>,
  companyId: string
): Promise<PreloadedImage[]> {
  const maxAttempts = 10;
  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
  const pending = new Set<number | string>(imageIds);
  const preloadedImages: PreloadedImage[] = [];

  // Step 1: Get signed URLs for all images (with retry logic)
  for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
    // Process pending images with bounded concurrency per attempt
    const results = await mapWithConcurrency(
      Array.from(pending),
      STORAGE_CONCURRENCY,
      async (imageId) => {
        try {
          const { data: imageData, error: imageError } = await supabase
            .from('imagenes_generadas')
            .select(
              'file_name, favorito, variante_id, variantes:variantes(nombre)',
            )
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imageError || !imageData?.file_name) return null;

          const filePath = `${companyId}/full/${imageData.file_name}`;

          // Reuse a still-fresh cached signed URL so repeat preloads don't
          // hit the Storage API again (one connection per request).
          let signedUrl = getCachedSignedUrl(filePath);
          if (!signedUrl) {
            const { data: signedUrlData } = await supabase.storage
              .from('generadas')
              .createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) return null;
            signedUrl = signedUrlData.signedUrl;
            setCachedSignedUrl(filePath, signedUrl);
          }

          const varianteNombre =
            (imageData as any).variante_id
              ? ((imageData as any).variantes?.nombre as string | undefined) ||
                undefined
              : undefined;

          return {
            imageId,
            fileName: imageData.file_name,
            signedUrl,
            favorito: imageData.favorito === true,
            variante_nombre: varianteNombre,
          };
        } catch (err) {
          console.error(`[preloadGeneratedImages] Error for image ${imageId}:`, err);
          return null;
        }
      },
    );

    for (const result of results) {
      if (result) {
        preloadedImages.push({
          id: result.imageId,
          fileName: result.fileName,
          signedUrl: result.signedUrl,
          favorito: result.favorito,
          variante_nombre: result.variante_nombre,
        });
        pending.delete(result.imageId);
      }
    }

    if (pending.size > 0 && attempt < maxAttempts) {
      await delay(1000);
    }
  }

  // Step 2: Firmar el opt/ (lo crea el backend) + precargar en la cache del browser
  await mapWithConcurrency(preloadedImages, STORAGE_CONCURRENCY, async (img) => {
    const optPath = `${companyId}/opt/${img.fileName}`;

    // Con la URL firmada ya en cache se evita hasta el createSignedUrl.
    const cachedOpt = getCachedSignedUrl(optPath);
    if (cachedOpt) {
      img.optSignedUrl = cachedOpt;
    } else {
      // El opt/ lo ESCRIBE el backend (step `thumbnail` de generate_image /
      // image_edit, 800px con ffmpeg). Acá sólo se firma. Antes se bajaba el full,
      // se reescalaba con canvas a 300px y se volvía a subir: además de duplicar
      // trabajo, era el upload más pesado de todo el preload y el que disparaba los
      // picos de connection pool. Si el thumbnail todavía no está (carrera con el
      // final del job), se cae al full — el fallback de siempre.
      try {
        const { data: optUrlData } = await supabase.storage
          .from('generadas')
          .createSignedUrl(optPath, 3600);
        if (optUrlData?.signedUrl) {
          img.optSignedUrl = optUrlData.signedUrl;
          setCachedSignedUrl(optPath, optUrlData.signedUrl);
        }
      } catch (err) {
        // Non-critical: sin opt/ se usa el full.
        console.warn(`[preloadGeneratedImages] no se pudo firmar el opt de ${img.fileName}:`, err);
      }
    }

    // Preload the smaller opt URL (or full as fallback) into browser cache.
    //
    // Con TOPE: `onload`/`onerror` cubren que la imagen cargue o falle, pero no que la
    // respuesta se quede colgada —una red que se traba y nunca contesta ni corta—. Sin
    // esto, esa sola imagen dejaba la función entera sin resolver, y con ella todo lo que
    // la espera: quien llama no recibe NI SIQUIERA las que sí cargaron
    // (ADR-2026-09-25-revision-greptile-la-precarga-siempre-termina).
    //
    // Calentar la caché del navegador es una mejora, no un requisito: pasado el tope se
    // sigue igual y la `<img>` de la pantalla la vuelve a pedir cuando se dibuje.
    await new Promise<void>((resolve) => {
      const image = new Image();
      const listo = () => {
        window.clearTimeout(tope);
        resolve();
      };
      const tope = window.setTimeout(listo, ESPERA_MAX_POR_IMAGEN_MS);
      image.onload = listo;
      image.onerror = listo;
      image.src = img.optSignedUrl || img.signedUrl;
    });
  });

  return preloadedImages;
}
